import React, { useState, useRef, useEffect } from 'react'
import { HiPlus, HiChevronDown, HiCash } from 'react-icons/hi'
import OptionsTagInput from './OptionsTagInput'
import { CURRENCIES } from './CurrencySelector'

function AddChoiceForm({
  newChoiceText,
  setNewChoiceText,
  loading,
  error,
  onSubmit,
  // New props for price/options
  currency,
  currencySymbol,
  newChoicePrice,
  setNewChoicePrice,
  newChoiceOptions,
  setNewChoiceOptions
}) {
  const [showDetails, setShowDetails] = useState(false)
  const detailsRef = useRef(null)

  // Scroll into view when expanded
  useEffect(() => {
    if (showDetails && detailsRef.current) {
      setTimeout(() => {
        detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }, 50)
    }
  }, [showDetails])

  // Get currency symbol helper
  const getCurrencySymbol = () => {
    if (!currency) return null
    const curr = CURRENCIES.find(c => c.code === currency)
    return curr ? curr.symbol : currency
  }

  const displaySymbol = currencySymbol || getCurrencySymbol()

  return (
    <div className="card fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
      <h3 style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '1.5rem'
      }}>
        <HiPlus style={{ fontSize: '1.5rem', color: 'var(--accent-secondary)' }} />
        Add a Choice
      </h3>
      <form onSubmit={onSubmit}>
        <div className="form-group">
          <input
            type="text"
            className="form-input"
            placeholder="Enter a new choice..."
            value={newChoiceText}
            onChange={(e) => setNewChoiceText(e.target.value)}
            disabled={loading}
            maxLength={100}
          />
          {error && (
            <div className="form-error" style={{
              animation: 'slideIn 0.3s ease-out',
              marginTop: '0.5rem'
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Collapsible details section - Modern interactive toggle */}
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: showDetails
              ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(139, 92, 246, 0.1))'
              : 'rgba(59, 130, 246, 0.05)',
            border: showDetails
              ? '1px solid rgba(59, 130, 246, 0.4)'
              : '1px dashed rgba(255, 255, 255, 0.15)',
            borderRadius: 'var(--radius-md)',
            color: showDetails ? 'var(--accent-primary)' : 'var(--text-muted)',
            fontSize: '0.8rem',
            fontWeight: '500',
            cursor: 'pointer',
            padding: '0.5rem 0.85rem',
            marginBottom: '0.75rem',
            transition: 'all 0.25s ease'
          }}
          onMouseEnter={(e) => {
            if (!showDetails) {
              e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)'
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)'
              e.currentTarget.style.borderStyle = 'solid'
              e.currentTarget.style.color = 'var(--accent-primary)'
            }
          }}
          onMouseLeave={(e) => {
            if (!showDetails) {
              e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)'
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)'
              e.currentTarget.style.borderStyle = 'dashed'
              e.currentTarget.style.color = 'var(--text-muted)'
            }
          }}
        >
          <HiCash style={{ fontSize: '1rem' }} />
          <span>{showDetails ? 'Hide price & options' : 'Add price & options'}</span>
          <span style={{
            fontSize: '0.7rem',
            opacity: 0.7,
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '0.15rem 0.4rem',
            borderRadius: '0.25rem'
          }}>
            optional
          </span>
          <span style={{
            transition: 'transform 0.2s ease',
            transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'flex',
            alignItems: 'center'
          }}>
            <HiChevronDown style={{ fontSize: '1rem' }} />
          </span>
        </button>

        {showDetails && (
          <div ref={detailsRef} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            padding: '0.75rem',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-md)',
            marginBottom: '1rem'
          }}>
            {/* Row 1: Price input + Options label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '0' }}>
              {/* Price input */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  Price:
                </span>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-sm)',
                  overflow: 'hidden',
                  width: '110px'
                }}>
                  {displaySymbol ? (
                    <span style={{
                      padding: '0.4rem 0.5rem',
                      background: 'rgba(59, 130, 246, 0.1)',
                      color: 'var(--accent-primary)',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      borderRight: '1px solid var(--border-color)'
                    }}>
                      {displaySymbol}
                    </span>
                  ) : (
                    <span style={{
                      padding: '0.4rem 0.5rem',
                      background: 'rgba(59, 130, 246, 0.1)',
                      color: 'var(--accent-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      borderRight: '1px solid var(--border-color)'
                    }}>
                      <HiCash style={{ fontSize: '0.9rem' }} />
                    </span>
                  )}
                  <input
                    type="number"
                    value={newChoicePrice ?? ''}
                    onChange={(e) => setNewChoicePrice(e.target.value === '' ? null : parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    disabled={loading}
                    min="0"
                    step="0.01"
                    style={{
                      flex: 1,
                      padding: '0.4rem 0.5rem',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text-primary)',
                      fontSize: '0.85rem',
                      outline: 'none',
                      width: '60px'
                    }}
                  />
                </div>
              </div>

              {/* Options label - inline with price */}
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Options (e.g., sizes, variations):
              </span>
            </div>

            {/* Row 2: Options tag input - full width */}
            <OptionsTagInput
              options={newChoiceOptions || []}
              onChange={setNewChoiceOptions}
              disabled={loading}
              maxTags={20}
              maxLength={50}
              placeholder="Add option..."
            />
          </div>
        )}

        <button
          type="submit"
          className={`btn btn-primary ${loading ? 'btn-loading' : ''}`}
          disabled={loading || !newChoiceText.trim()}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
        >
          <HiPlus />
          {loading ? 'Adding...' : 'Add Choice'}
        </button>
      </form>
    </div>
  )
}

export default AddChoiceForm
